import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import PageHeader from '@/components/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import Combobox from '@/components/ui/Combobox.jsx';
import {
  createLocalidadAtendida,
  deleteLocalidadAtendida,
  getApiErrorMessage,
  listEmpresasRedespacho,
  listLocalidadesAtendidas,
  listLocalidadesOperativas,
  listProvinciasOperativas,
  listSucursalesOperativas,
  lookupLocalidadAtendida,
  updateLocalidadAtendida,
} from '@/services/sucursalesOperativas.js';

const inputClassName = 'h-8.5 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.88)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:bg-[rgba(var(--subpanel),0.75)]';
const panelClassName = 'rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] p-2.5 shadow-[0_10px_30px_rgba(var(--shadow),0.12)] md:p-3';

const emptyForm = {
  id: null,
  sucursal_id: '',
  provincia_id: '',
  localidad_id: '',
  localidad_ids: [],
  prioridad: '1',
  principal: true,
  activa: true,
  atiende_directo: true,
  atiende_redespacho: false,
  empresa_redespacho_id: '',
  observaciones_redespacho: '',
  observaciones: '',
};

function BooleanPill({ value, trueLabel = 'Si', falseLabel = 'No' }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${value ? 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.14)] text-[rgba(var(--muted),0.95)]'}`}>
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function getModalidadMeta(row) {
  const directo = Boolean(row?.atiende_directo);
  const redespacho = Boolean(row?.atiende_redespacho);
  if (directo && redespacho) {
    return {
      label: 'Directo + Redespacho',
      className: 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]',
    };
  }
  if (redespacho) {
    return {
      label: 'Redespacho',
      className: 'bg-[rgba(234,88,12,0.14)] text-[rgb(234,88,12)]',
    };
  }
  return {
    label: 'Directo',
    className: 'bg-[rgba(34,197,94,0.14)] text-[rgb(22,163,74)]',
  };
}

export default function LocalidadesAtendidasPage() {
  const [rows, setRows] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [empresasRedespacho, setEmpresasRedespacho] = useState([]);
  const [localidadesForm, setLocalidadesForm] = useState([]);
  const [localidadesLookup, setLocalidadesLookup] = useState([]);
  const [filters, setFilters] = useState({ sucursal_id: '', provincia_id: '', localidad_id: '', activa: '1', q: '' });
  const [form, setForm] = useState(emptyForm);
  const [lookup, setLookup] = useState({ sucursal_origen_id: '', provincia_id: '', localidad_id: '' });
  const [lookupResult, setLookupResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localidadPickerValue, setLocalidadPickerValue] = useState('');

  const loadSucursales = async () => {
    const data = await listSucursalesOperativas();
    setSucursales(Array.isArray(data) ? data : []);
  };

  const loadProvincias = async () => {
    const data = await listProvinciasOperativas();
    setProvincias(Array.isArray(data) ? data : []);
  };

  const loadEmpresasRedespacho = async () => {
    const data = await listEmpresasRedespacho();
    setEmpresasRedespacho(Array.isArray(data) ? data : []);
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const data = await listLocalidadesAtendidas({
        sucursal_id: filters.sucursal_id ? Number(filters.sucursal_id) : undefined,
        provincia_id: filters.provincia_id ? Number(filters.provincia_id) : undefined,
        localidad_id: filters.localidad_id ? Number(filters.localidad_id) : undefined,
        activa: filters.activa,
        q: filters.q || undefined,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      const message = getApiErrorMessage(error, 'No se pudieron cargar las localidades atendidas');
      console.error('Error cargando localidades atendidas:', error);
      setRows([]);
      await Swal.fire('Error', message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadFormLocalidades = async (provinciaId, preserveValue = false, nextValue = '') => {
    if (!provinciaId) {
      setLocalidadesForm([]);
      setForm((current) => ({
        ...current,
        localidad_id: preserveValue ? current.localidad_id : '',
        localidad_ids: preserveValue ? current.localidad_ids : [],
      }));
      setLocalidadPickerValue('');
      return;
    }

    const data = await listLocalidadesOperativas({ provincia_id: Number(provinciaId) });
    const nextItems = Array.isArray(data) ? data : [];
    setLocalidadesForm(nextItems);

    if (preserveValue && nextItems.some((item) => String(item.id) === String(nextValue))) {
      setForm((current) => ({ ...current, localidad_id: String(nextValue), localidad_ids: [String(nextValue)] }));
      return;
    }

    setForm((current) => ({ ...current, localidad_id: '', localidad_ids: [] }));
    setLocalidadPickerValue('');
  };

  const loadLookupLocalidades = async (provinciaId) => {
    if (!provinciaId) {
      setLocalidadesLookup([]);
      setLookup((current) => ({ ...current, localidad_id: '' }));
      return;
    }

    const data = await listLocalidadesOperativas({ provincia_id: Number(provinciaId) });
    setLocalidadesLookup(Array.isArray(data) ? data : []);
    setLookup((current) => ({ ...current, localidad_id: '' }));
  };

  useEffect(() => {
    Promise.all([loadSucursales(), loadProvincias(), loadEmpresasRedespacho()]).catch(async (error) => {
      const message = getApiErrorMessage(error, 'No se pudo inicializar la pantalla');
      await Swal.fire('Error', message, 'error');
    });
  }, []);

  useEffect(() => {
    loadRows();
  }, [filters.sucursal_id, filters.provincia_id, filters.localidad_id, filters.activa, filters.q]);

  const filteredRows = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.sucursal_nombre, row.numero_sucursal, row.provincia_nombre, row.localidad_nombre, row.empresa_redespacho_nombre]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [rows, filters.q]);

  const validateForm = () => {
    const hasLocalidad = form.id
      ? Boolean(form.localidad_id)
      : Array.isArray(form.localidad_ids) && form.localidad_ids.length > 0;

    if (!form.sucursal_id || !form.provincia_id || !hasLocalidad) {
      Swal.fire('Atencion', 'Sucursal, provincia y localidad son obligatorias.', 'warning');
      return false;
    }
    if (!form.atiende_directo && !form.atiende_redespacho) {
      Swal.fire('Atencion', 'Debe marcar al menos una modalidad de atencion.', 'warning');
      return false;
    }
    if (form.atiende_redespacho && !form.empresa_redespacho_id) {
      Swal.fire('Atencion', 'Seleccione la empresa responsable del redespacho.', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm(emptyForm);
    setLocalidadesForm([]);
    setLocalidadPickerValue('');
  };

  const handleEdit = async (row) => {
    const nextForm = {
      id: row.id,
      sucursal_id: row.sucursal_id ? String(row.sucursal_id) : '',
      provincia_id: row.provincia_id ? String(row.provincia_id) : '',
      localidad_id: row.localidad_id ? String(row.localidad_id) : '',
      localidad_ids: row.localidad_id ? [String(row.localidad_id)] : [],
      prioridad: String(row.prioridad || 1),
      principal: Boolean(row.principal),
      activa: Boolean(row.activa),
      atiende_directo: Boolean(row.atiende_directo ?? true),
      atiende_redespacho: Boolean(row.atiende_redespacho),
      empresa_redespacho_id: row.empresa_redespacho_id ? String(row.empresa_redespacho_id) : '',
      observaciones_redespacho: row.observaciones_redespacho || '',
      observaciones: row.observaciones || '',
    };

    setForm(nextForm);
    setLocalidadPickerValue(nextForm.localidad_id || '');
    await loadFormLocalidades(nextForm.provincia_id, true, nextForm.localidad_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddLocalidad = (localidadId) => {
    if (!localidadId) return;
    const nextValue = String(localidadId);

    setForm((current) => {
      if (current.id) {
        return {
          ...current,
          localidad_id: nextValue,
          localidad_ids: [nextValue],
        };
      }

      const currentIds = Array.isArray(current.localidad_ids) ? current.localidad_ids.map(String) : [];
      if (currentIds.includes(nextValue)) {
        return current;
      }

      return {
        ...current,
        localidad_id: current.localidad_id || nextValue,
        localidad_ids: [...currentIds, nextValue],
      };
    });

    setLocalidadPickerValue(currentValue => (form.id ? nextValue : ''));
  };

  const handleRemoveLocalidad = (localidadId) => {
    const targetId = String(localidadId);
    setForm((current) => {
      const nextIds = (current.localidad_ids || []).map(String).filter((id) => id !== targetId);
      return {
        ...current,
        localidad_ids: nextIds,
        localidad_id: current.localidad_id === targetId ? (nextIds[0] || '') : current.localidad_id,
      };
    });
    if (form.id && String(form.localidad_id) === targetId) {
      setLocalidadPickerValue('');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (form.id) {
        const payload = {
          ...form,
          sucursal_id: Number(form.sucursal_id),
          provincia_id: Number(form.provincia_id),
          localidad_id: Number(form.localidad_id),
          prioridad: Number(form.prioridad || 1),
          empresa_redespacho_id: form.atiende_redespacho && form.empresa_redespacho_id ? Number(form.empresa_redespacho_id) : null,
        };
        await updateLocalidadAtendida(form.id, payload);
        resetForm();
        await loadRows();
        await Swal.fire('Listo', 'Relacion guardada correctamente', 'success');
      } else {
        const localidadIds = (form.localidad_ids || []).map((id) => Number(id)).filter(Boolean);
        const localidadNombreById = new Map(localidadesForm.map((item) => [Number(item.id), item.nombre]));
        const successNames = [];
        const failedItems = [];

        for (const localidadId of localidadIds) {
          try {
            await createLocalidadAtendida({
              ...form,
              sucursal_id: Number(form.sucursal_id),
              provincia_id: Number(form.provincia_id),
              localidad_id: localidadId,
              prioridad: Number(form.prioridad || 1),
              empresa_redespacho_id: form.atiende_redespacho && form.empresa_redespacho_id ? Number(form.empresa_redespacho_id) : null,
            });
            successNames.push(localidadNombreById.get(localidadId) || `Localidad #${localidadId}`);
          } catch (error) {
            failedItems.push({
              name: localidadNombreById.get(localidadId) || `Localidad #${localidadId}`,
              reason: getApiErrorMessage(error, 'No se pudo relacionar'),
            });
          }
        }

        await loadRows();

        if (!successNames.length && failedItems.length) {
          await Swal.fire({
            title: 'No se pudieron guardar las relaciones',
            html: `<ul style="text-align:left">${failedItems.map((item) => `<li><strong>${item.name}</strong>: ${item.reason}</li>`).join('')}</ul>`,
            icon: 'warning',
          });
          return;
        }

        resetForm();

        const successHtml = successNames.length
          ? `<p style="text-align:left;margin:0 0 8px 0;"><strong>Asignadas correctamente:</strong> ${successNames.join(', ')}</p>`
          : '';
        const failedHtml = failedItems.length
          ? `<p style="text-align:left;margin:8px 0;"><strong>No se pudieron relacionar:</strong></p><ul style="text-align:left">${failedItems.map((item) => `<li><strong>${item.name}</strong>: ${item.reason}</li>`).join('')}</ul>`
          : '';

        await Swal.fire({
          title: failedItems.length ? 'Carga parcial completada' : 'Listo',
          html: `${successHtml}${failedHtml}`,
          icon: failedItems.length ? 'info' : 'success',
        });
      }
    } catch (error) {
      await Swal.fire('Error', getApiErrorMessage(error, 'No se pudo guardar la relacion'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const localidadOptions = useMemo(
    () => localidadesForm.map((item) => ({ value: String(item.id), label: item.nombre })),
    [localidadesForm],
  );

  const empresaRedespachoOptions = useMemo(
    () => empresasRedespacho.map((item) => ({ value: String(item.id), label: `${item.codigo} · ${item.nombre}` })),
    [empresasRedespacho],
  );

  const localidadesSeleccionadas = useMemo(
    () => localidadesForm.filter((item) => (form.localidad_ids || []).map(String).includes(String(item.id))),
    [form.localidad_ids, localidadesForm],
  );

  const handleDeactivate = async (row) => {
    const decision = await Swal.fire({
      title: row.activa ? 'Desactivar relacion?' : 'La relacion ya esta inactiva',
      icon: row.activa ? 'question' : 'info',
      showCancelButton: row.activa,
    });
    if (!decision.isConfirmed) return;

    try {
      await deleteLocalidadAtendida(row.id);
      await loadRows();
    } catch (error) {
      await Swal.fire('Error', getApiErrorMessage(error, 'No se pudo desactivar la relacion'), 'error');
    }
  };

  const handleLookup = async () => {
    if (!lookup.sucursal_origen_id) {
      setLookupResult({ error: 'Seleccione la sucursal origen para validar el routing real.' });
      return;
    }

    if (!lookup.localidad_id) {
      setLookupResult({ error: 'Seleccione una localidad para resolver la sucursal responsable.' });
      return;
    }

    try {
      const data = await lookupLocalidadAtendida({
        sucursal_origen_id: lookup.sucursal_origen_id ? Number(lookup.sucursal_origen_id) : undefined,
        localidad_id: Number(lookup.localidad_id),
      });
      setLookupResult(data);
    } catch (error) {
      setLookupResult({ error: getApiErrorMessage(error, 'No se pudo resolver la sucursal operativa') });
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-2 px-2 py-2 md:px-3 md:py-3">
      <PageHeader
        title="Localidades atendidas"
        subtitle=""
        actions={
          <div className="flex flex-wrap gap-2">
            <a href="/administracion/redespachos" className="inline-flex h-9 items-center rounded-xl border border-[rgb(var(--border))] px-3 text-sm font-medium text-[rgb(var(--fg))]">Empresas de redespacho</a>
            <a href="/administracion/empresa/rutas-de-distribucion" className="inline-flex h-9 items-center rounded-xl border border-[rgb(var(--border))] px-3 text-sm font-medium text-[rgb(var(--fg))]">Rutas via</a>
          </div>
        }
      />

      <section className={`${panelClassName} grid grid-cols-12 gap-2`}>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Sucursal responsable</label>
          <select className={inputClassName} value={form.sucursal_id} onChange={(e) => setForm((current) => ({ ...current, sucursal_id: e.target.value }))}>
            <option value="">Seleccionar sucursal</option>
            {sucursales.map((item) => <option key={item.id} value={item.id}>{item.numero_sucursal || item.id} - {item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Provincia</label>
          <select className={inputClassName} value={form.provincia_id} onChange={async (e) => {
            const provinciaId = e.target.value;
            setForm((current) => ({ ...current, provincia_id: provinciaId, localidad_id: '', localidad_ids: [] }));
            setLocalidadPickerValue('');
            await loadFormLocalidades(provinciaId);
          }}>
            <option value="">Seleccionar provincia</option>
            {provincias.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Localidad</label>
          <Combobox
            options={localidadOptions}
            value={localidadPickerValue}
            onChange={(value) => handleAddLocalidad(value)}
            placeholder={form.provincia_id ? (form.id ? 'Buscar localidad' : 'Escribir para agregar localidades') : 'Elegir provincia primero'}
            searchPlaceholder="Buscar localidad..."
            emptyMessage="No se encontraron localidades"
            disabled={!form.provincia_id}
          />
          {!form.id && localidadesSeleccionadas.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {localidadesSeleccionadas.map((item) => (
                <span key={item.id} className="inline-flex items-center gap-2 rounded-full bg-[rgba(var(--brand),0.14)] px-3 py-1 text-xs font-semibold text-[rgb(var(--brand))]">
                  {item.nombre}
                  <button type="button" className="rounded-full px-1 text-[rgb(var(--brand))]" onClick={() => handleRemoveLocalidad(item.id)}>x</button>
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="col-span-6 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Prioridad</label>
          <input className={inputClassName} value={form.prioridad} onChange={(e) => setForm((current) => ({ ...current, prioridad: e.target.value.replace(/\D/g, '') || '1' }))} />
        </div>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Observaciones</label>
          <input className={inputClassName} value={form.observaciones} onChange={(e) => setForm((current) => ({ ...current, observaciones: e.target.value }))} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Empresa de redespacho</label>
          <Combobox
            options={empresaRedespachoOptions}
            value={form.empresa_redespacho_id}
            onChange={(value) => setForm((current) => ({ ...current, empresa_redespacho_id: value || '' }))}
            placeholder={form.atiende_redespacho ? 'Buscar empresa responsable' : 'Marque redespacho para seleccionar'}
            searchPlaceholder="Buscar empresa..."
            emptyMessage="No se encontraron empresas"
            disabled={!form.atiende_redespacho}
          />
        </div>
        <div className="col-span-12 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Obs. redespacho</label>
          <input className={inputClassName} value={form.observaciones_redespacho} onChange={(e) => setForm((current) => ({ ...current, observaciones_redespacho: e.target.value }))} disabled={!form.atiende_redespacho} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="grid grid-cols-1 gap-2 rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.34)] p-3">
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input type="checkbox" checked={form.principal} onChange={(e) => setForm((current) => ({ ...current, principal: e.target.checked }))} />
              <span>Sucursal principal</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input type="checkbox" checked={form.activa} onChange={(e) => setForm((current) => ({ ...current, activa: e.target.checked }))} />
              <span>Relacion activa</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input type="checkbox" checked={form.atiende_directo} onChange={(e) => setForm((current) => ({ ...current, atiende_directo: e.target.checked }))} />
              <span>Atiende en forma directa</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input
                type="checkbox"
                checked={form.atiende_redespacho}
                onChange={(e) => setForm((current) => ({
                  ...current,
                  atiende_redespacho: e.target.checked,
                  empresa_redespacho_id: e.target.checked ? current.empresa_redespacho_id : '',
                  observaciones_redespacho: e.target.checked ? current.observaciones_redespacho : '',
                }))}
              />
              <span>Atiende por redespacho</span>
            </label>
          </div>
        </div>
        <div className="col-span-12">
          <StickyActions
            onCancel={resetForm}
            onSubmit={handleSubmit}
            isSubmitting={saving}
            primaryLabel={form.id ? 'Guardar cambios' : 'Crear relacion'}
            secondaryLabel="Limpiar"
          />
        </div>
      </section>

      <section className={`${panelClassName} grid grid-cols-12 gap-2`}>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Sucursal</label>
          <select className={inputClassName} value={filters.sucursal_id} onChange={(e) => setFilters((current) => ({ ...current, sucursal_id: e.target.value }))}>
            <option value="">Todas las sucursales</option>
            {sucursales.map((item) => <option key={item.id} value={item.id}>{item.numero_sucursal || item.id} - {item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Provincia</label>
          <select className={inputClassName} value={filters.provincia_id} onChange={(e) => setFilters((current) => ({ ...current, provincia_id: e.target.value }))}>
            <option value="">Todas las provincias</option>
            {provincias.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Estado</label>
          <select className={inputClassName} value={filters.activa} onChange={(e) => setFilters((current) => ({ ...current, activa: e.target.value }))}>
            <option value="">Todas</option>
            <option value="1">Activas</option>
            <option value="0">Inactivas</option>
          </select>
        </div>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Busqueda</label>
          <input className={inputClassName} placeholder="Localidad o sucursal" value={filters.q} onChange={(e) => setFilters((current) => ({ ...current, q: e.target.value }))} />
        </div>
        <div className="col-span-12">
          {loading ? (
            <div className="py-8 text-center text-sm text-[rgba(var(--muted),0.88)]">Cargando localidades atendidas...</div>
          ) : filteredRows.length === 0 ? (
            <EmptyState title="Sin localidades parametrizadas" description="Cree la primera relacion entre localidad y sucursal para dejar operativo el destino logistico." />
          ) : (
            <div className="max-h-[40vh] overflow-auto rounded-2xl border border-[rgba(var(--border),0.55)]">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[rgba(var(--subpanel),0.58)] text-[rgba(var(--muted),0.95)]">
                  <tr>
                    <th className="px-3 py-2">Sucursal</th>
                    <th className="px-3 py-2">Provincia</th>
                    <th className="px-3 py-2">Localidad</th>
                    <th className="px-3 py-2">Modalidad</th>
                    <th className="px-3 py-2">Empresa redespacho</th>
                    <th className="px-3 py-2">Principal</th>
                    <th className="px-3 py-2">Activa</th>
                    <th className="px-3 py-2">Prioridad</th>
                    <th className="px-3 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id} className="border-t border-[rgba(var(--border),0.42)]">
                      <td className="px-3 py-2">
                        <div className="font-medium">{row.sucursal_nombre}</div>
                        <div className="text-xs text-[rgba(var(--muted),0.88)]">Nro {row.numero_sucursal || row.sucursal_id}</div>
                      </td>
                      <td className="px-3 py-2">{row.provincia_nombre}</td>
                      <td className="px-3 py-2">{row.localidad_nombre}</td>
                      <td className="px-3 py-2">
                        {(() => {
                          const modalidad = getModalidadMeta(row);
                          return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${modalidad.className}`}>{modalidad.label}</span>;
                        })()}
                      </td>
                      <td className="px-3 py-2">{row.empresa_redespacho_nombre || '-'}</td>
                      <td className="px-3 py-2"><BooleanPill value={Boolean(row.principal)} /></td>
                      <td className="px-3 py-2"><BooleanPill value={Boolean(row.activa)} /></td>
                      <td className="px-3 py-2">{row.prioridad || 1}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleEdit(row)}>Editar</button>
                          <button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleDeactivate(row)}>{row.activa ? 'Desactivar' : 'Inactiva'}</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className={`${panelClassName} grid grid-cols-12 gap-2`}>
        <div className="col-span-12"><h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Consulta para emision de guias</h2></div>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Sucursal origen</label>
          <select className={inputClassName} value={lookup.sucursal_origen_id} onChange={(e) => setLookup((current) => ({ ...current, sucursal_origen_id: e.target.value }))}>
            <option value="">Seleccionar sucursal origen</option>
            {sucursales.map((item) => <option key={item.id} value={item.id}>{item.numero_sucursal || item.id} - {item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Provincia destino</label>
          <select className={inputClassName} value={lookup.provincia_id} onChange={async (e) => {
            const provinciaId = e.target.value;
            setLookup((current) => ({ ...current, provincia_id: provinciaId }));
            await loadLookupLocalidades(provinciaId);
          }}>
            <option value="">Seleccionar provincia</option>
            {provincias.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Localidad destino</label>
          <select className={inputClassName} value={lookup.localidad_id} onChange={(e) => setLookup((current) => ({ ...current, localidad_id: e.target.value }))} disabled={!lookup.provincia_id}>
            <option value="">{lookup.provincia_id ? 'Seleccionar localidad' : 'Elegir provincia primero'}</option>
            {localidadesLookup.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-2 flex items-end">
          <button type="button" className="h-9 w-full rounded-xl border border-[rgb(var(--border))] px-3 text-sm font-medium" onClick={handleLookup}>Consultar</button>
        </div>
        <div className="col-span-12 md:col-span-10">
          <div className="rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.3)] p-3 text-sm">
            {lookupResult?.error ? (
              <span className="text-[rgb(var(--danger,220_38_38))]">{lookupResult.error}</span>
            ) : lookupResult ? (
              <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                <div><strong>Sucursal responsable:</strong> {lookupResult.sucursal_destino_nombre || `Sucursal #${lookupResult.sucursal_destino_id}`}</div>
                <div><strong>Sucursal via:</strong> {lookupResult.sucursal_via_nombre || 'Directo'}</div>
                <div><strong>Modo:</strong> {lookupResult.routing_mode || 'directo'}</div>
                <div><strong>Modalidad:</strong> {lookupResult.modalidad_atencion || 'DIRECTA'}</div>
                <div><strong>Empresa redespacho:</strong> {lookupResult.empresa_redespacho_nombre || 'No aplica'}</div>
                <div><strong>Requiere recanalizacion:</strong> {lookupResult.requiere_recanalizacion ? 'Si' : 'No'}</div>
              </div>
            ) : (
              <span className="text-[rgba(var(--muted),0.88)]">Seleccione una localidad para ver que sucursal la atiende y dejar lista la integracion con emision de guias.</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
